import { createLazyModule } from '@/utils/lazy';
import { colours } from '@/ux/colours';

export const DEFAULT_TIME_FORMAT = 'MMM D';
export const TIME_FORMATS = {
  day: 'MMM D',
  month: 'MMMM YYYY',
};

const chartjsModule = createLazyModule(async () => {
  const [
    {
      Chart,
      BarElement,
      BarController,
      LineController,
      DoughnutController,
      LineElement,
      PointElement,
      ArcElement,
      LinearScale,
      CategoryScale,
      TimeScale,
      Filler,
      Tooltip,
      Legend,
    },
    { WordCloudController, WordElement },
  ] = await Promise.all([import('chart.js'), import('chartjs-chart-wordcloud')]);

  // Must load after chart.js — adapter accesses _adapters at evaluation time
  await import('@/components/foundation/charts/chartjs-adapter-dayjs');

  Chart.register(
    BarElement,
    BarController,
    LineController,
    DoughnutController,
    WordCloudController,
    WordElement,
    LineElement,
    PointElement,
    ArcElement,
    LinearScale,
    CategoryScale,
    TimeScale,
    Filler,
    Tooltip,
    Legend,
  );

  Chart.defaults.font.family = "'Inter', 'Arial', sans-serif";
  Chart.defaults.font.size = 10;
  Chart.defaults.color = colours.TEXT.TEXT_SECONDARY;

  Chart.defaults.hover.mode = 'nearest';
  Chart.defaults.hover.intersect = false;

  Chart.defaults.maintainAspectRatio = false;

  Chart.defaults.layout.padding.top = 2;
  Chart.defaults.layout.padding.left = 2;
  Chart.defaults.layout.padding.bottom = 2;
  Chart.defaults.layout.padding.right = 2;

  Chart.defaults.datasets.line.tension = 0.4;
  Chart.defaults.datasets.line.borderColor = colours.CHART.SET.CHART_SET_01;
  Chart.defaults.datasets.line.borderWidth = 2.5;
  Chart.defaults.datasets.line.pointRadius = 5;
  Chart.defaults.datasets.line.pointBackgroundColor = colours.CHART.SET.CHART_SET_01;
  Chart.defaults.datasets.line.pointBorderColor = 'white';
  Chart.defaults.datasets.line.pointBorderWidth = 3;
  Chart.defaults.datasets.line.pointHoverBackgroundColor = 'white';
  Chart.defaults.datasets.line.pointHoverBorderColor = colours.CHART.SET.CHART_SET_01;
  Chart.defaults.datasets.line.pointHoverBorderWidth = 3;

  Chart.defaults.datasets.bar.borderRadius = 3;
  Chart.defaults.datasets.bar.maxBarThickness = 50;

  Chart.defaults.plugins.legend.display = false;

  return { Chart };
});

export function loadChartjs() {
  return chartjsModule.load();
}

export function isChartjsLoaded() {
  return chartjsModule.isLoaded();
}

export function resetChartjs() {
  chartjsModule.reset();
}

const chartDataLabelsModule = createLazyModule(async () => {
  const mod = await import('chartjs-plugin-datalabels');
  return { ChartDataLabels: mod.default ?? mod };
});

export function loadChartDataLabels() {
  return chartDataLabelsModule.load();
}
